/**
 * ViewModeToggle component.
 *
 * A compact Radix ToggleGroup with 3 options:
 *   - Zones: concentric-rings SVG visualization (default)
 *   - Strip: horizontal zone-panel list
 *   - Plan: floor plan overlay (disabled when site has no blueprint)
 *
 * Selection is persisted to localStorage under 'signalsentry:viewMode'.
 *
 * Accessibility:
 * - Each ToggleGroup.Item is a <button> with aria-label describing the mode
 * - Disabled Plan item includes tooltip "No blueprint uploaded"
 * - Focus ring 2px cyan per global CSS (focus-visible)
 *
 * Design spec reference: section 3.3
 */
import * as ToggleGroup from '@radix-ui/react-toggle-group';
import * as Tooltip from '@radix-ui/react-tooltip';
import { CircleHalf, Rows, MapTrifold } from '@phosphor-icons/react';
import { theme } from '@/design/tokens.css';
import * as styles from './ViewModeToggle.css';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** The three available visualization modes. */
export type ViewMode = 'zones' | 'strip' | 'plan';

interface Props {
  /** Currently active mode. */
  value: ViewMode;
  /** Callback when the user selects a different mode. */
  onChange: (mode: ViewMode) => void;
  /** When false, the Plan option is disabled with a tooltip. */
  hasBlueprint: boolean;
}

// ---------------------------------------------------------------------------
// Sub-component: TooltipItem
// ---------------------------------------------------------------------------

/**
 * A ToggleGroup.Item wrapped in a Radix Tooltip.
 *
 * The tooltip fires on hover after a short delay.
 * When disabled, the tooltip explains why.
 */
function TooltipItem({
  value,
  ariaLabel,
  tooltipText,
  disabled,
  children,
}: {
  value: ViewMode;
  ariaLabel: string;
  tooltipText: string;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Tooltip.Root delayDuration={300}>
      <Tooltip.Trigger asChild>
        {/* Wrap in span so Tooltip.Trigger can attach ref to a DOM element
            even when ToggleGroup.Item is disabled. */}
        <span style={{ display: 'inline-flex' }}>
          <ToggleGroup.Item
            value={value}
            aria-label={ariaLabel}
            disabled={disabled}
            className={styles.item}
          >
            {children}
          </ToggleGroup.Item>
        </span>
      </Tooltip.Trigger>
      <Tooltip.Portal>
        {/*
          Tooltip popover styling routes through theme tokens (vanilla-extract
          var() references) so the popover adopts the active SOC palette in
          both light and dark mode. Previous values were hardcoded HSL strings
          that bypassed the data-theme attribute selector.
        */}
        <Tooltip.Content
          side="bottom"
          sideOffset={6}
          style={{
            background: theme.colors.bg.surface1,
            color: theme.colors.text.primary,
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '11px',
            fontFamily: theme.fonts.sans,
            letterSpacing: '0.02em',
            boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
            border: `1px solid ${theme.colors.border.default}`,
          }}
        >
          {tooltipText}
          <Tooltip.Arrow style={{ fill: theme.colors.border.default }} />
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/** localStorage key for view mode persistence. */
const STORAGE_KEY = 'signalsentry:viewMode';

/**
 * ViewModeToggle: segmented control for the /now visualization mode.
 *
 * @param value - Currently selected mode
 * @param onChange - Called when user selects a new mode
 * @param hasBlueprint - Controls whether the Plan option is enabled
 */
export function ViewModeToggle({ value, onChange, hasBlueprint }: Props) {
  function handleChange(newValue: string) {
    if (!newValue) return; // Radix calls with '' when clicking the active item (deselect prevention)
    localStorage.setItem(STORAGE_KEY, newValue);
    onChange(newValue as ViewMode);
  }

  return (
    <Tooltip.Provider>
      <ToggleGroup.Root
        type="single"
        value={value}
        onValueChange={handleChange}
        className={styles.root}
        aria-label="Visualization mode"
      >
        <TooltipItem value="zones" ariaLabel="Zones view" tooltipText="Concentric zones">
          <CircleHalf weight="fill" size={14} />
        </TooltipItem>

        <TooltipItem value="strip" ariaLabel="Strip view" tooltipText="Zone strip">
          <Rows size={14} />
        </TooltipItem>

        <TooltipItem
          value="plan"
          ariaLabel="Floor plan view"
          tooltipText={hasBlueprint ? 'Floor plan' : 'No blueprint uploaded'}
          disabled={!hasBlueprint}
        >
          <MapTrifold size={14} />
        </TooltipItem>
      </ToggleGroup.Root>
    </Tooltip.Provider>
  );
}
